import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, usePassword, useTokens, useUpdateUser } from '@pkg/hooks';

import config from '@app/config';
import { setMobileUserNavigationIsOpen } from '@app/store/actions/dashboard';
import { addNotification } from '@app/store/actions/notification';
import { initializeUser } from '@app/store/actions/user';

import YatPasswordInput from '../Input/PasswordInput';

import './_SetPasswordForm.scss';

interface SetPasswordFormProps {
    hasPassword: boolean;
    isReset?: boolean;
}

const SetPasswordForm = ({ hasPassword, isReset }: SetPasswordFormProps) => {
    const { t } = useTranslation(['account', 'translation', 'common']);
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [password, setPassword] = useState('');
    const { mutateAsync: updateUser } = useUpdateUser();
    const { state: locationState } = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { resetPassword } = usePassword();
    const { setTokens } = useTokens();
    const [searchParams] = useSearchParams();
    const walletLinkCreation = locationState?.walletLinkCreation;

    const resetToken = searchParams.get('token');

    useEffect(() => {
        if (isReset && !resetToken) {
            navigate(config.homeUrl);
        }
    }, [isReset, navigate, resetToken]);

    const postSubmitUserInit = useCallback(
        async (tokens) => {
            setTokens(tokens);
            await dispatch(initializeUser());
            dispatch(setMobileUserNavigationIsOpen(false));
        },
        [dispatch, setTokens],
    );

    const setNewPassword = useCallback(() => {
        const successLink = walletLinkCreation ? `/partner/${walletLinkCreation}/manage` : config.homeUrl;
        updateUser({ value: { current_password: currentPassword, password }, dispatch }).then(async (r) => {
            if (!r.ok && r.fields) {
                if (r.fields['current_password']) {
                    setCurrentPasswordError(t('common:error_password_invalid'));
                }
            }
            if (r.ok && r.value) {
                postSubmitUserInit(r.value.token_response).then(() => {
                    dispatch(
                        addNotification({
                            message: t('common:password_updated', { updatedCopy: hasPassword ? 'updated' : 'set' }),
                            title: `${t('words:success')}!`,
                            severity: 'alert',
                            duration: 6000,
                        }),
                    );
                    navigate(successLink);
                });
            }
        });
    }, [
        currentPassword,
        dispatch,
        hasPassword,
        navigate,
        password,
        postSubmitUserInit,
        t,
        updateUser,
        walletLinkCreation,
    ]);

    const handleResetPassword = useCallback(() => {
        if (resetToken) {
            resetPassword({ password, token: resetToken }).then((r) => {
                if (!r.ok && r.error?.detail === 'Password has already been reset.') {
                    navigate('/forgot-password');
                }
                if (r.ok && r.value) {
                    postSubmitUserInit(r.value).then(() => {
                        dispatch(
                            addNotification({
                                message: t('common:password_updated', { updatedCopy: 'updated' }),
                                title: `${t('words:success')}!`,
                                severity: 'alert',
                                duration: 6000,
                            }),
                        );
                    });
                }
            });
        }
    }, [dispatch, navigate, password, postSubmitUserInit, resetPassword, resetToken, t]);

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            if (password.length < 8) {
                setPasswordError(t('account:password_too_short'));
                return;
            }
            if (isReset) {
                handleResetPassword();
            } else {
                setNewPassword();
            }
        },
        [isReset, password.length, handleResetPassword, setNewPassword, t],
    );

    const handleCurrentPasswordChange = (event) => {
        setCurrentPassword(event.target.value);
        setCurrentPasswordError('');
    };

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        setPasswordError('');
    };

    const showCurrentPWInput = !isReset && hasPassword;

    return (
        <form className="set-password-form__form" onSubmit={handleSubmit}>
            {showCurrentPWInput ? (
                <YatPasswordInput
                    classes="user-account-page__input"
                    isInvalid={!!currentPasswordError}
                    autoFocus
                    onChange={handleCurrentPasswordChange}
                    placeholder={t('common:current_password')}
                    value={currentPassword}
                    errorLabel={currentPasswordError}
                />
            ) : null}
            <YatPasswordInput
                classes="user-account-page__input"
                isInvalid={!!passwordError}
                onChange={handlePasswordChange}
                placeholder={hasPassword ? t('common:new_password') : t('common:password')}
                value={password}
                errorLabel={passwordError}
            />
            <div className="user-account-page__btn">
                <DepButton display={DepButtonDisplay.minWidth} variant="large" type={'submit'}>
                    {t('common:lets_do_this')}
                </DepButton>
            </div>
        </form>
    );
};

export default SetPasswordForm;
